import {
	Box,
	Button,
	Grid,
	Typography,
	useMediaQuery,
	useTheme,
	Dialog,
	DialogActions,
	DialogTitle,
	Link,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FipDiscoveredAccountsSection from './FipDiscoveredAccountsSection';
import Footer from '../Sections/Footer';
import linkedAccountsSectionStyles from '../Accounts/LinkedAccountsSection.styles';
import discoveryStyles from './AccountDiscoveryContainer.styles';
import { useState, useEffect, useContext, useRef } from 'react';
import FipNotDiscoveredAccountsSection from './FipNotDiscoveredAccountsSection';
import { isAccountLinked, getUniqueKey } from '../../utils/helper.utils';
import {
	isAllMandatoryAccountsLinked,
	filterLinkedAccountsByRestrictions,
	getTargetedFips,
	isDisplayOnlyTargetedFipsEnabled,
	isTerminateOnNoAccountFoundEnabled,
} from '../../utils/targetedAccounts.utils';
import StepperContext from '../Context/StepperContext';
import { useTranslation } from '../../hooks/useTranslation';
import { PAGE_HEADING_ID } from '../../utils/pageHeading.utils';
import BackIconButton from '../common/BackIconButton';
import { toast } from 'react-toastify';
import AuthApis from '../../hepler/authApis.service';
import LinkingOtpModal from './LinkingOtpModal';
import SharingDetailsModal from './SharingDetailsModal';
import { postMessage } from '../../utils/common.utils';
import {
	isSequentialLinkingEnabled,
	isShowSharingDetailsLinkEnabled,
} from '../../utils/entityConfig.utils';
import { getEntityThemedCtaButtonSx } from '../../utils/ctaContrast.utils';
import { keyboardFocusVisibleSx } from '../../utils/a11y.utils';

function AccountDiscoveryContainer(props) {
	const { t } = useTranslation();
	const { nextStep } = useContext(StepperContext);
	const theme = useTheme();
	const mobileScreen = useMediaQuery(theme.breakpoints.down('md'));

	// Check for dark theme
	const isDark =
		props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const authApis = new AuthApis();
	const [changingMobileNumber, setChangingMobileNumber] = useState(false);
	const [openDialog, setOpenDialog] = useState(false);
	const [sharingDetailsOpen, setSharingDetailsOpen] = useState(false);
	const [accountsNotLinked, setAccountsNotLinked] = useState([]);
	const [accountsNotLinkedLength, setAccountsNotLinkedLength] = useState(0);
	const [selectedAccountLinkedLength, setSelectedAccountLinkedLength] =
		useState(0);
	const [allLinkedAccounts, setAllLinkedAccounts] = useState(
		props.accountState.linkedAccountsFull,
	);
	const [sessionLinkedAccounts, setSessionLinkedAccounts] = useState([]);

	// ── Sequential linking state ──────────────────────────────────────────────
	// selectedAccountsByFip: { [fipId]: Account[] } — tracks per-FIP selection
	const [selectedAccountsByFip, setSelectedAccountsByFip] = useState({});

	// Linking modal / drawer state
	const [linkingState, setLinkingState] = useState({
		queue: [], // [{ fipId, fipName, phoneNumber, accounts }]
		currentIndex: -1,
		open: false,
		otp: '',
		isResend: false,
		time: 60,
		linkingRefNo: '',
		isSubmitting: false,
		linkedStatus: null, // null = OTP entry, [] = success
	});
	const timerRef = useRef(null);

	const appendSessionLinkedAccounts = (linkedAccounts) => {
		if (!linkedAccounts?.length) return;
		setSessionLinkedAccounts((prev) => {
			const merged = [...prev];
			linkedAccounts.forEach((account) => {
				const exists = merged.some(
					(existing) =>
						existing.accRefNumber === account.accRefNumber &&
						existing.fipId === account.fipId,
				);
				if (!exists) {
					merged.push(account);
				}
			});
			return merged;
		});
	};

	const getDiscoveryPreselectAccounts = () => {
		const allSelected = Object.values(selectedAccountsByFip).flat();
		if (allSelected.length === 0) {
			return sessionLinkedAccounts.length > 0
				? sessionLinkedAccounts
				: undefined;
		}
		const seen = new Set();
		return allSelected.filter((acc) => {
			const key = `${acc.fipId}:${acc.accRefNumber}`;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	};

	const buildConsentNavigationState = (accountsToPass) => ({
		data: accountsToPass,
		preselectAccounts: getDiscoveryPreselectAccounts(),
	});

	// ── Sequential linking helpers ────────────────────────────────────────────

	const stopTimer = () => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
	};

	const startTimer = () => {
		stopTimer();
		setLinkingState((prev) => ({ ...prev, time: 60, isResend: false }));
		timerRef.current = setInterval(() => {
			setLinkingState((prev) => {
				const newTime = prev.time - 1;
				if (newTime <= 0) {
					stopTimer();
					return { ...prev, time: 60, isResend: true };
				}
				return { ...prev, time: newTime };
			});
		}, 1000);
	};

	/** Call accountLinking API for the given queue item and start OTP timer. */
	const triggerAccountLinking = (queueItem) => {
		const accountsToLink = queueItem.accounts.map(
			({ restrictions, fipId: _fipId, ...rest }) => rest,
		);
		authApis
			.accountLinking(queueItem.fipId, accountsToLink)
			.then((res) => {
				if (!res) {
					toast.error(t('errors.general.somethingWentWrong'));
					return;
				}
				if (res.status === 'SUCCESS') {
					setLinkingState((prev) => ({
						...prev,
						linkingRefNo: res.RefNumber,
						isResend: false,
					}));
					startTimer();
				} else {
					const errMsg = res?.code
						? t(`errors.code.${res.code}`)
						: res?.message;
					toast.error(errMsg);
					setLinkingState((prev) => ({ ...prev, isResend: true }));
				}
			})
			.catch(() => {
				toast.error(t('errors.general.failedToLink'));
				setLinkingState((prev) => ({ ...prev, isResend: true }));
			});
	};

	/** Advance to next FIP in queue or navigate to consent when done. */
	const advanceOrFinish = (
		queue,
		nextIndex,
		currentAllLinked,
		currentSessionLinked,
	) => {
		stopTimer();
		if (nextIndex < queue.length) {
			// Brief pause to let success state render, then move to next FIP
			setTimeout(() => {
				setLinkingState((prev) => ({
					...prev,
					currentIndex: nextIndex,
					otp: '',
					isResend: false,
					time: 60,
					linkingRefNo: '',
					isSubmitting: false,
					linkedStatus: null,
				}));
				triggerAccountLinking(queue[nextIndex]);
			}, 2000);
		} else {
			// All FIPs done — close modal and navigate
			setTimeout(() => {
				setLinkingState((prev) => ({ ...prev, open: false }));
				let accountsToPass =
					global.redirectAccountRestrictions && !global.userClickedAddMore
						? filterLinkedAccountsByRestrictions(currentAllLinked)
						: currentAllLinked;
				if (isDisplayOnlyTargetedFipsEnabled()) {
					const targetedFips = getTargetedFips();
					if (targetedFips.length > 0) {
						accountsToPass = accountsToPass.filter((acc) =>
							targetedFips.includes(acc.fipId),
						);
					}
				}
				if (
					isTerminateOnNoAccountFoundEnabled() &&
					accountsToPass.length === 0
				) {
					const targetedFips = getTargetedFips();
					props.history.push('/exit', {
						fipIds: targetedFips.length > 0 ? targetedFips : [],
					});
					return;
				}
				props.history.push({
					pathname: `/payment-consent`,
					state: {
						data: accountsToPass,
						preselectAccounts: getDiscoveryPreselectAccounts(),
					},
				});
				nextStep(1);
			}, 2000);
		}
	};

	/** Called when accountConfirmLinking succeeds for current FIP. */
	const handleLinkingSuccess = (newLinkedAccounts, queue, currentIndex) => {
		// Compute merged linked accounts synchronously so we can pass them forward
		const updatedAllLinked = [...allLinkedAccounts];
		newLinkedAccounts.forEach((acc) => {
			if (!updatedAllLinked.some((e) => e.accRefNumber === acc.accRefNumber)) {
				updatedAllLinked.push(acc);
			}
		});

		const updatedSessionLinked = [...sessionLinkedAccounts];
		newLinkedAccounts.forEach((acc) => {
			if (
				!updatedSessionLinked.some(
					(e) => e.accRefNumber === acc.accRefNumber && e.fipId === acc.fipId,
				)
			) {
				updatedSessionLinked.push(acc);
			}
		});

		setAllLinkedAccounts(updatedAllLinked);
		setSessionLinkedAccounts(updatedSessionLinked);
		setAccountsNotLinkedLength((prev) => prev - newLinkedAccounts.length);
		setSelectedAccountLinkedLength((prev) => prev + 1);

		setLinkingState((prev) => ({
			...prev,
			isSubmitting: false,
			linkedStatus: newLinkedAccounts,
		}));

		advanceOrFinish(
			queue,
			currentIndex + 1,
			updatedAllLinked,
			updatedSessionLinked,
		);
	};

	/** Skip current FIP (user cancelled) — advance to next or finish. */
	const handleSkipFip = () => {
		const { queue, currentIndex } = linkingState;
		stopTimer();
		if (currentIndex + 1 < queue.length) {
			setLinkingState((prev) => ({
				...prev,
				currentIndex: currentIndex + 1,
				otp: '',
				isResend: false,
				time: 60,
				linkingRefNo: '',
				isSubmitting: false,
				linkedStatus: null,
			}));
			triggerAccountLinking(queue[currentIndex + 1]);
		} else {
			setLinkingState((prev) => ({ ...prev, open: false }));
		}
	};

	/** Submit OTP for current FIP. */
	const handleOtpConfirm = () => {
		const { queue, currentIndex, otp, linkingRefNo } = linkingState;
		const currentFip = queue[currentIndex];
		const selectedAccsForFip =
			selectedAccountsByFip[currentFip?.fipId] || currentFip?.accounts || [];

		setLinkingState((prev) => ({ ...prev, isSubmitting: true }));

		authApis
			.accountConfirmLinking(linkingRefNo, otp, currentFip.fipId)
			.then((res) => {
				if (res.status === 'SUCCESS') {
					const newLinkedAccounts = res.AccLinkDetails.map((apiAcc) => {
						const match = selectedAccsForFip.find(
							(s) => s.accRefNumber === apiAcc.accRefNumber,
						);
						return { ...(match || {}), ...apiAcc, fipId: currentFip.fipId };
					});
					toast.success(res.message);
					handleLinkingSuccess(newLinkedAccounts, queue, currentIndex);
				} else {
					setLinkingState((prev) => ({
						...prev,
						otp: '',
						isSubmitting: false,
					}));
					const errMsg = res?.code
						? t(`errors.code.${res.code}`)
						: res?.message;
					toast.error(errMsg);
					if (res.status === 'FAILURE') {
						postMessage('session', 'FAILURE');
					}
				}
			})
			.catch(() => {
				setLinkingState((prev) => ({ ...prev, isSubmitting: false }));
				toast.error(t('errors.general.somethingWentWrong'));
			});
	};

	/** Start the sequential linking flow from a pre-built queue. */
	const startSequentialLinking = async (queue) => {
		// Fetch linkingOtpLength for all FIPs in parallel before opening the modal
		const enrichedQueue = await Promise.all(
			queue.map(async (item) => {
				try {
					const res = await authApis.fipDetails(item.fipId);
					const otpLength = res?.FipDetails?.[0]?.linkingOtpLength;
					return { ...item, linkingOtpLength: otpLength ?? null };
				} catch {
					return { ...item, linkingOtpLength: null };
				}
			}),
		);
		setLinkingState({
			queue: enrichedQueue,
			currentIndex: 0,
			open: true,
			otp: '',
			isResend: false,
			time: 60,
			linkingRefNo: '',
			isSubmitting: false,
			linkedStatus: null,
		});
		triggerAccountLinking(enrichedQueue[0]);
	};

	/** Navigate to consent screen (shared helper). */
	const navigateToConsent = (overrideLinked) => {
		const linked = overrideLinked || allLinkedAccounts;
		let accountsToPass =
			global.redirectAccountRestrictions && !global.userClickedAddMore
				? filterLinkedAccountsByRestrictions(linked)
				: linked;
		if (isDisplayOnlyTargetedFipsEnabled()) {
			const targetedFips = getTargetedFips();
			if (targetedFips.length > 0) {
				accountsToPass = accountsToPass.filter((acc) =>
					targetedFips.includes(acc.fipId),
				);
			}
		}
		if (isTerminateOnNoAccountFoundEnabled() && accountsToPass.length === 0) {
			const targetedFips = getTargetedFips();
			props.history.push('/exit', {
				fipIds: targetedFips.length > 0 ? targetedFips : [],
			});
			return;
		}
		props.history.push({
			pathname: `/payment-consent`,
			state: buildConsentNavigationState(accountsToPass),
		});
		nextStep(1);
	};
	// ─────────────────────────────────────────────────────────────────────────

	// Merge restrictions into already-linked accounts on mount
	useEffect(() => {
		if (
			global.redirectAccountRestrictions &&
			global.redirectAccountRestrictions.length > 0 &&
			props.accountState.linkedAccountsFull.length > 0
		) {
			// Only process if accounts don't already have restrictions
			const needsProcessing = props.accountState.linkedAccountsFull.some(
				(acc) => !acc.restrictions,
			);

			if (needsProcessing) {
				const accountsWithRestrictions =
					props.accountState.linkedAccountsFull.map((account) => {
						const matchingRestriction = global.redirectAccountRestrictions.find(
							(restriction) => {
								const fipIdMatch = account.fipId === restriction.fipId;
								const fiTypeMatch = account.FIType === restriction.fiType;
								const accTypeMatch = account.accType === restriction.accType;
								// Compare last 4 digits: restriction might have just "6086" while account has "XXXXX6086"
								const maskedMatch =
									account.maskedAccNumber === restriction.maskedAccNumber ||
									account.maskedAccNumber?.slice(-4) ===
										restriction.maskedAccNumber;
								return fipIdMatch && fiTypeMatch && accTypeMatch && maskedMatch;
							},
						);

						if (matchingRestriction) {
							return { ...account, restrictions: matchingRestriction };
						}
						return account;
					});

				setAllLinkedAccounts(accountsWithRestrictions);
			}
		}
	}, [props.accountState.linkedAccountsFull]);

	// Logic to calculate discovered accounts that are not yet linked
	useEffect(() => {
		const accountsNotLinkedSet = new Set();

		const allDiscoveredAccounts = props.accountState.discoveredAccounts.flatMap(
			(fip) =>
				(fip.discoveredAccounts || []).map((acc) => ({
					...acc,
					fipId: fip.fipId,
				})),
		);

		const unlinkedAccounts = allDiscoveredAccounts.filter((account) => {
			const uniqueKey = getUniqueKey(account, account.fipId);
			if (!accountsNotLinkedSet.has(uniqueKey)) {
				accountsNotLinkedSet.add(uniqueKey);
				return !isAccountLinked(account, allLinkedAccounts, account.fipId);
			}
			return false;
		});

		setAccountsNotLinked(unlinkedAccounts);
		setAccountsNotLinkedLength(unlinkedAccounts.length);
	}, [
		props.accountState.discoveredAccounts,
		props.accountState.linkedAccountsFull,
		allLinkedAccounts,
	]);

	const sdkOptions = props.customDetails?.sdkOptions ?? global.sdkOptions;
	const isNewDiscoveryExperience = isSequentialLinkingEnabled(sdkOptions);

	const getSelectedUnlinkedAccountsCount = () =>
		Object.entries(selectedAccountsByFip).reduce((count, [fipId, accounts]) => {
			const unlinkedSelected = accounts.filter(
				(acc) => !isAccountLinked(acc, allLinkedAccounts, fipId),
			);
			return count + unlinkedSelected.length;
		}, 0);

	const hasSelectedOnlyLinkedAccounts = () => {
		const allSelected = Object.entries(selectedAccountsByFip).flatMap(
			([fipId, accounts]) => accounts.map((acc) => ({ ...acc, fipId })),
		);
		return (
			allSelected.length > 0 &&
			allSelected.every((acc) =>
				isAccountLinked(acc, allLinkedAccounts, acc.fipId),
			)
		);
	};

	const shouldShowUnlinkedWarningDialog = () => accountsNotLinkedLength > 0;

	// Legacy: warn when unlinked accounts remain and user has not linked anything
	// in this session. Pre-linked accounts from other FIPs do not suppress the dialog.
	const shouldShowLegacyContinueDialog = () =>
		accountsNotLinkedLength > 0 && selectedAccountLinkedLength === 0;

	const handleContinueClick = () => {
		// 1. Validate mandatory accounts
		if (
			global.redirectAccountRestrictions &&
			!isAllMandatoryAccountsLinked(
				props.accountState.discoveredAccounts,
				allLinkedAccounts,
			)
		) {
			toast.error(t('accountDiscovery.errors.linkMandatoryAccounts'));
			return;
		}

		// Legacy flow when new discovery experience is disabled
		if (!isNewDiscoveryExperience) {
			if (shouldShowLegacyContinueDialog()) {
				setOpenDialog(true);
			} else {
				navigateToConsent();
			}
			return;
		}

		// 2. Build linking queue: FIPs that have non-linked selected accounts
		const queue = props.accountState.discoveredAccounts.flatMap((fip) => {
			const fipId = fip.fipId;
			const selected = selectedAccountsByFip[fipId] || [];
			const toLink = selected.filter(
				(acc) => !isAccountLinked(acc, allLinkedAccounts, fipId),
			);
			if (toLink.length === 0) return [];
			return [
				{
					fipId,
					fipName: global.objectFormatFip[fipId]?.fipId || fipId,
					phoneNumber: fip.phoneNumber,
					accounts: toLink,
				},
			];
		});

		// 3. If there are selected unlinked accounts, start sequential linking
		if (queue.length > 0) {
			startSequentialLinking(queue);
			return;
		}

		// 4. No selected unlinked accounts — warn if unlinked still remain
		if (hasSelectedOnlyLinkedAccounts() || !shouldShowUnlinkedWarningDialog()) {
			navigateToConsent();
		} else {
			setOpenDialog(true);
		}
	};
	const handleDialogClose = (proceed) => {
		setOpenDialog(false);
		if (proceed) {
			navigateToConsent();
		}
	};

	const getContinueButtonLabel = () => {
		if (props.accountState.isDiscovering) {
			return t('common.buttons.discovering');
		}
		return isNewDiscoveryExperience && getSelectedUnlinkedAccountsCount() > 0
			? t('accountDiscovery.messages.proceedToLinkAccounts')
			: t('common.buttons.continue');
	};

	const continueButtonEndIcon =
		props.accountState.isDiscovering || !isNewDiscoveryExperience ? null : (
			<ArrowForwardIcon />
		);

	const continueButtonSxNew = {
		whiteSpace: 'nowrap',
		textTransform: 'none',
		flexShrink: 0,
		width: 'auto',
		minWidth: 'fit-content',
		maxWidth: '100%',
		px: { md: 2, lg: 2.5 },
		boxShadow: 'none',
		'&:hover': {
			boxShadow: 'none',
		},
	};

	const continueButtonSxHeader = {
		...continueButtonSxNew,
		ml: 'auto',
	};

	const headingContainerSxNew = {
		gap: '1rem',
		width: '100%',
		minWidth: 0,
		flexWrap: 'wrap',
		rowGap: '0.75rem',
	};

	const headingSxNew = {
		flex: '1 1 auto',
		minWidth: 0,
	};

	const getSeeWhatYouAreSharingLabel = () =>
		global.alternativeText?.seeWhatYouAreSharing ||
		t('accountDiscovery.messages.seeWhatYouAreSharing');

	const showSharingDetailsLink = isShowSharingDetailsLinkEnabled(sdkOptions);
	return (
		<>
			<Dialog
				open={openDialog}
				onClose={() => handleDialogClose(false)}
				aria-labelledby="account-discovery-continue-dialog-title"
			>
				<DialogTitle id="account-discovery-continue-dialog-title">
					{t('accountDiscovery.messages.continueDialogheading')}
				</DialogTitle>
				<DialogActions>
					<Button
						variant="outlined"
						onClick={() => handleDialogClose(false)}
						sx={{
							backgroundColor: isDark
								? 'transparent !important'
								: '#ffffff !important',
							color: `${theme.palette.error.main} !important`,
							borderColor: `${theme.palette.error.main} !important`,
							'&:hover': {
								backgroundColor: 'rgba(198, 40, 40, 0.04) !important',
								borderColor: `${theme.palette.error.main} !important`,
							},
						}}
					>
						{t('common.buttons.cancel')}
					</Button>
					<Button
						variant="contained"
						onClick={() => handleDialogClose(true)}
						sx={getEntityThemedCtaButtonSx(props.customDetails, isDark, {
							border: `1px solid ${
								props?.customDetails?.ctaBorderColor ??
								props?.customDetails?.primaryColor
							}`,
						})}
					>
						{t('common.buttons.proceed')}
					</Button>
				</DialogActions>
			</Dialog>
			<Box
				sx={{
					...discoveryStyles.container,
					overflowY: mobileScreen ? 'scroll' : 'visible',
					padding: mobileScreen
						? `2rem 1.5rem ${linkedAccountsSectionStyles.mobileContentPaddingWithActions} 1.5rem`
						: '2rem 1.875rem 4rem 1.875rem',
					minWidth: mobileScreen ? '100vw' : 'auto',
					backgroundColor: isDark ? '#161616' : theme.palette.primary.light,
					color: isDark ? '#FFFFFF' : '#000000',
					gap: mobileScreen ? '1.25rem' : '1.5rem',
					mb: mobileScreen ? '4rem' : 0,
					...(isNewDiscoveryExperience && { width: '100%' }),
				}}
			>
				<Box width={'100%'}>
					<Box
						sx={{
							...discoveryStyles.headingContainer,
							...(isNewDiscoveryExperience ? headingContainerSxNew : {}),
						}}
					>
						<Box
							sx={{
								...discoveryStyles.heading,
								...(isNewDiscoveryExperience ? headingSxNew : {}),
							}}
						>
							{!mobileScreen && global.isAddAccount ? (
								<BackIconButton
									sx={{ ml: '-10px' }}
									color="primary"
									onClick={props.handleBack}
								/>
							) : null}
							<Typography
								id={PAGE_HEADING_ID}
								component="h1"
								sx={{
									...discoveryStyles.headingText,
									color: isDark ? '#FFFFFF' : 'inherit',
								}}
							>
								{t('accountDiscovery.titles.verifyAccounts')}
							</Typography>
						</Box>
						{!mobileScreen ? (
							<Button
								size="large"
								variant="contained"
								onClick={handleContinueClick}
								disabled={props.accountState.isDiscovering}
								endIcon={continueButtonEndIcon}
								sx={{
									...discoveryStyles.nextButton,
									...(isNewDiscoveryExperience ? continueButtonSxHeader : {}),
									...getEntityThemedCtaButtonSx(props.customDetails, isDark),
									opacity: props.accountState.isDiscovering ? 0.7 : 1,
									position: 'relative',
									'&::after': props.accountState.isDiscovering
										? {
												content: '""',
												position: 'absolute',
												top: 0,
												left: 0,
												right: 0,
												bottom: 0,
												background:
													'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
												backgroundSize: '200% 100%',
												animation: 'loading 2s infinite linear',
												'@keyframes loading': {
													'0%': { backgroundPosition: '200% 0' },
													'100%': { backgroundPosition: '-200% 0' },
												},
											}
										: {},
								}}
							>
								{getContinueButtonLabel()}
							</Button>
						) : null}
					</Box>
					{showSharingDetailsLink && (
						<Link
							component="button"
							onClick={() => setSharingDetailsOpen(true)}
							sx={{
								display: 'block',
								mt: '0.25rem',
								mb: '0.5rem',
								p: '0.25rem',
								border: 'none',
								background: 'none',
								cursor: 'pointer',
								textAlign: 'left',
								fontSize: '0.875rem',
								fontWeight: 600,
								textDecoration: 'underline',
								color: isDark ? '#7EB8FF' : theme.palette.primary.main,
								fontFamily: props.customDetails?.fontFamily,
								borderRadius: '4px',
								...keyboardFocusVisibleSx,
								'&:hover': {
									textDecoration: 'underline',
									opacity: 0.85,
								},
							}}
						>
							{getSeeWhatYouAreSharingLabel()}
						</Link>
					)}
					<Typography
						color={
							props.customDetails?.secondaryTextColor ??
							theme.palette.text.secondary
						}
						sx={discoveryStyles.subTitleText}
					>
						{t('accountDiscovery.messages.linkAccountsWithOtp')}
						<span
							style={{
								color: isDark ? '#FFFFFF' : theme.palette.info.main,
								fontWeight: '700',
							}}
						>
							{' '}
							{props?.accountState?.noDiscoveredAccounts?.phoneNumber
								? props?.accountState?.noDiscoveredAccounts?.phoneNumber
								: global.mobileNumber
									? global.mobileNumber
									: global.mobileNumberArray[0]}{' '}
						</span>
						{/* {global.sdkOptions.allowDifferentMobile==="Y"&&
            <Link
              color={theme.palette.primary.main}
              onClick={()=>{ props.history.push(`/NoAccountFound/${global.mobileNumber}?step=${101}&fip=${props.match.params.fid}`) }}
              sx={discoveryStyles.changeNumberLink}
            >
              CHANGE MOBILE  .<br />
            </Link>}*/}
					</Typography>
				</Box>
				<Grid
					container
					sx={{
						...discoveryStyles.discoveredAccountsContainer,
						...(isNewDiscoveryExperience && {
							width: '100%',
							display: 'grid',
							gridTemplateColumns: {
								xs: '1fr',
								md: 'repeat(3, minmax(0, 1fr))',
							},
							'& > .MuiGrid-item': {
								width: '100%',
								maxWidth: '100%',
							},
						}),
					}}
				>
					{(() => {
						return props.accountState.discoveredAccounts.map(
							(accountsSingleFip) => {
								return (
									<FipDiscoveredAccountsSection
										key={accountsSingleFip.fipId}
										changingMobileNumber={changingMobileNumber}
										setChangingMobileNumber={setChangingMobileNumber}
										accountsSingleFip={accountsSingleFip}
										accountsNotLinkedLength={accountsNotLinkedLength}
										setAccountsNotLinkedLength={setAccountsNotLinkedLength}
										selectedAccountLinkedLength={selectedAccountLinkedLength}
										setSelectedAccountLinkedLength={
											setSelectedAccountLinkedLength
										}
										allLinkedAccounts={allLinkedAccounts}
										setAllLinkedAccounts={setAllLinkedAccounts}
										onSessionAccountsLinked={appendSessionLinkedAccounts}
										onSelectedAccountsChange={
											isNewDiscoveryExperience
												? (fipId, accounts) =>
														setSelectedAccountsByFip((prev) => ({
															...prev,
															[fipId]: accounts,
														}))
												: undefined
										}
										{...props}
									/>
								);
							},
						);
					})()}
					{!props.accountState.isDiscovering &&
						props.accountState.noDiscoveredAccounts?.fips?.length > 0 &&
						// Hide "no account found" section after retry exhaustion with partial match
						!(
							props.retryCount >= props.maxRetries &&
							global.redirectAccountRestrictions &&
							props.accountState.discoveredAccounts.length > 0
						) && (
							<FipNotDiscoveredAccountsSection
								changingMobileNumber={changingMobileNumber}
								setChangingMobileNumber={setChangingMobileNumber}
								{...props}
							/>
						)}
				</Grid>
			</Box>

			{mobileScreen ? (
				<Box
					sx={{
						...linkedAccountsSectionStyles.mobileBottomStack,
						backgroundColor: isDark
							? '#161616'
							: props.customDetails?.secondaryColor || '#ffffff',
					}}
				>
					<Box
						sx={{
							...linkedAccountsSectionStyles.mobileFixedButtonGroup,
							borderTop: '1px solid #E0E0E0',
							color: isDark ? '#FFFFFF' : '#000000',
						}}
					>
						{global.isAddAccount ? (
							<Box
								sx={{
									display: 'flex',
									width: '100%',
									gap: '1rem',
									flexWrap: 'wrap',
								}}
							>
								<Button
									variant="outlined"
									fullWidth={mobileScreen}
									color="primary"
									onClick={() => props.handleBack()}
									sx={{
										flex: 1,
										backgroundColor: 'transparent',
										color: isDark
											? '#FFFFFF'
											: props.customDetails?.primaryColor ||
												theme.palette.primary.main,
										borderColor: isDark
											? '#FFFFFF'
											: props.customDetails?.primaryColor ||
												theme.palette.primary.main,
									}}
								>
									{t('common.buttons.back')}
								</Button>
								<Button
									variant="contained"
									fullWidth={mobileScreen}
									onClick={handleContinueClick}
									disabled={props.accountState.isDiscovering}
									endIcon={continueButtonEndIcon}
									sx={{
										...(isNewDiscoveryExperience ? continueButtonSxNew : {}),
										...getEntityThemedCtaButtonSx(props.customDetails, isDark, {
											flex: isNewDiscoveryExperience ? 2 : 1,
											border: `1px solid ${
												props?.customDetails?.ctaBorderColor ??
												props?.customDetails?.primaryColor
											}`,
											'&:hover': {
												border: props?.customDetails?.ctaHoverBorderColor
													? `1px solid ${props?.customDetails?.ctaHoverBorderColor}`
													: `1px solid ${props?.customDetails?.primaryColor}`,
											},
											'&.Mui-disabled': {
												border: props?.customDetails?.ctaDisableBorderColor
													? `1px solid ${props?.customDetails?.ctaDisableBorderColor}`
													: '0',
											},
										}),
										opacity: props.accountState.isDiscovering ? 0.7 : 1,
										position: 'relative',
										'&::after': props.accountState.isDiscovering
											? {
													content: '""',
													position: 'absolute',
													top: 0,
													left: 0,
													right: 0,
													bottom: 0,
													background:
														'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
													backgroundSize: '200% 100%',
													animation: 'loading 2s infinite linear',
													'@keyframes loading': {
														'0%': { backgroundPosition: '200% 0' },
														'100%': { backgroundPosition: '-200% 0' },
													},
												}
											: {},
									}}
								>
									{getContinueButtonLabel()}
								</Button>
							</Box>
						) : null}
						{!global.isAddAccount && (
							<Box
								sx={{
									display: 'flex',
									width: '100%',
									gap: '1rem',
									flexWrap: 'wrap',
								}}
							>
								<Button
									variant="contained"
									fullWidth={mobileScreen}
									onClick={handleContinueClick}
									disabled={props.accountState.isDiscovering}
									endIcon={continueButtonEndIcon}
									sx={{
										...(isNewDiscoveryExperience ? continueButtonSxNew : {}),
										...getEntityThemedCtaButtonSx(props.customDetails, isDark, {
											flex: 1,
										}),
										opacity: props.accountState.isDiscovering ? 0.7 : 1,
										position: 'relative',
										'&::after': props.accountState.isDiscovering
											? {
													content: '""',
													position: 'absolute',
													top: 0,
													left: 0,
													right: 0,
													bottom: 0,
													background: `linear-gradient(90deg, transparent 0%, ${
														isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.3)'
													} 50%, transparent 100%)`,
													backgroundSize: '200% 100%',
													animation: 'loading 2s infinite linear',
													'@keyframes loading': {
														'0%': { backgroundPosition: '200% 0' },
														'100%': { backgroundPosition: '-200% 0' },
													},
												}
											: {},
									}}
								>
									{getContinueButtonLabel()}
								</Button>
							</Box>
						)}
					</Box>
					<Footer embedded customDetails={props.customDetails} />
				</Box>
			) : (
				<Footer fixed customDetails={props.customDetails} />
			)}

			{showSharingDetailsLink && (
				<SharingDetailsModal
					open={sharingDetailsOpen}
					onClose={() => setSharingDetailsOpen(false)}
					customDetails={props.customDetails}
					consentDetails={props.accountState?.consentDetails}
				/>
			)}

			{isNewDiscoveryExperience && (
				<LinkingOtpModal
					open={linkingState.open}
					onClose={handleSkipFip}
					fipItem={linkingState.queue[linkingState.currentIndex]}
					otp={linkingState.otp}
					onOtpChange={(val) =>
						setLinkingState((prev) => ({ ...prev, otp: val }))
					}
					isResend={linkingState.isResend}
					time={linkingState.time}
					isSubmitting={linkingState.isSubmitting}
					linkedStatus={linkingState.linkedStatus}
					onResend={() => {
						const current = linkingState.queue[linkingState.currentIndex];
						if (current) triggerAccountLinking(current);
					}}
					onSubmit={handleOtpConfirm}
					customDetails={props.customDetails}
				/>
			)}
		</>
	);
}

export default AccountDiscoveryContainer;
